/**
 * Video embed utilities for Homepage Video Blocks
 * Supports both video URLs and Vimeo iframe embed codes
 */

/**
 * Whitelist of allowed iframe src domains for video embeds
 */
const ALLOWED_VIDEO_DOMAINS = [
  'player.vimeo.com',
  'www.youtube.com',
  'youtube.com',
];

/**
 * Allowed iframe attributes for security
 */
const ALLOWED_IFRAME_ATTRIBUTES = [
  'src',
  'width',
  'height',
  'frameborder',
  'allow',
  'allowfullscreen',
  'title',
  'style',
];

export interface VideoEmbedResult {
  isValid: boolean;
  embedUrl: string | null;
  error: string | null;
}

/**
 * Sanitizes and validates Vimeo/YouTube iframe embed code.
 * Returns the src URL if valid, or an error message if invalid.
 */
export function sanitizeVideoEmbedCode(embedCode: string): VideoEmbedResult {
  if (!embedCode || typeof embedCode !== 'string') {
    return { isValid: false, embedUrl: null, error: 'Embed code is required' };
  }

  const trimmed = embedCode.trim();

  // Check for disallowed content
  if (/<script/i.test(trimmed)) {
    return { isValid: false, embedUrl: null, error: 'Script tags are not allowed' };
  }

  if (/on\w+\s*=/i.test(trimmed)) {
    return { isValid: false, embedUrl: null, error: 'Event handlers are not allowed' };
  }

  // Extract iframe src using regex
  const iframeMatch = trimmed.match(/<iframe[^>]*>/i);
  if (!iframeMatch) {
    return { isValid: false, embedUrl: null, error: 'Invalid embed code: must contain an <iframe> tag' };
  }

  const iframeTag = iframeMatch[0];
  
  // Extract src attribute
  const srcMatch = iframeTag.match(/src\s*=\s*["']([^"']+)["']/i);
  if (!srcMatch) {
    return { isValid: false, embedUrl: null, error: 'Invalid embed code: iframe must have a src attribute' };
  }

  const srcUrl = srcMatch[1];

  // Validate that src is from an allowed domain
  try {
    const url = new URL(srcUrl);
    const hostname = url.hostname.toLowerCase();
    
    const isAllowedDomain = ALLOWED_VIDEO_DOMAINS.some(
      domain => hostname === domain || hostname.endsWith('.' + domain)
    );

    if (!isAllowedDomain) {
      return { 
        isValid: false, 
        embedUrl: null, 
        error: `Invalid embed source. Only Vimeo and YouTube embeds are allowed.` 
      };
    }

    // Ensure HTTPS
    if (url.protocol !== 'https:') {
      // Convert to HTTPS
      url.protocol = 'https:';
    }

    return { isValid: true, embedUrl: url.toString(), error: null };
  } catch {
    return { isValid: false, embedUrl: null, error: 'Invalid URL in embed code' };
  }
}

/**
 * Converts a regular YouTube or Vimeo URL to an embed-friendly URL.
 * Returns the original URL if it's already an embed URL or not a recognized video platform.
 */
export function getVideoEmbedUrl(url: string): string {
  if (!url) return url;

  // Already an embed URL - return as-is
  if (url.includes("youtube.com/embed/") || url.includes("player.vimeo.com/video/")) {
    return url;
  }

  // YouTube: youtube.com/watch?v=VIDEO_ID or youtu.be/VIDEO_ID
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  );
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  // Vimeo: vimeo.com/VIDEO_ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  // Not a recognized video URL - return as-is
  return url;
}

/**
 * Determines if a string looks like an embed code (contains iframe) or a URL
 */
export function isEmbedCode(value: string): boolean {
  if (!value) return false;
  return /<iframe/i.test(value.trim());
}

/**
 * Gets the final embed URL from either a URL or embed code
 */
export function getVideoEmbedUrlFromInput(value: string, inputType: 'url' | 'embed'): VideoEmbedResult {
  if (!value) {
    return { isValid: false, embedUrl: null, error: null };
  }

  if (inputType === 'embed') {
    return sanitizeVideoEmbedCode(value);
  }

  // URL mode - use the existing URL conversion
  const embedUrl = getVideoEmbedUrl(value);
  return { isValid: true, embedUrl, error: null };
}
